import { useMutation, useQueryClient } from "@tanstack/react-query";
import { saveAccountCertificates } from "../../../shared/api/account/saveCertificates";
import { HttpError } from "../../../shared/api/http";
import type {
	SaveCertificatesRequest,
	SaveCertificatesResponse,
} from "../../../shared/types/save-certificates.type";
import { accountCardQueryKey } from "./useAccountCard";
import { normalizeAccountCardId } from "../../../shared/utils/normalize-account-card-id";

export function extractSaveCertificatesError(error: unknown): string {
	if (!(error instanceof HttpError)) {
		return error instanceof Error
			? error.message
			: "Не вдалося зберегти сертифікати";
	}

	if (error.status === 404) {
		return "Обліковий запис не знайдено";
	}

	const fromPayload = formatValidationPayload(error.payload);
	if (error.status === 422) {
		return fromPayload || "Невірні дані сертифікатів";
	}

	if (error.status === 502 || error.status === 504 || error.status >= 500) {
		return fromPayload || "Сервіс сертифікатів тимчасово недоступний";
	}

	return fromPayload || error.message;
}

function formatValidationPayload(payload: unknown): string | undefined {
	if (!payload || typeof payload !== "object") return undefined;
	const record = payload as Record<string, unknown>;
	const detail = record.detail;

	if (typeof detail === "string") return detail;

	if (Array.isArray(detail)) {
		const parts = detail
			.map((item) => {
				if (!item || typeof item !== "object") return null;
				const row = item as Record<string, unknown>;
				if (typeof row.msg === "string") return row.msg;
				if (typeof row.message === "string") return row.message;
				return null;
			})
			.filter(Boolean);
		if (parts.length) return parts.join("; ");
	}

	if (typeof record.message === "string") return record.message;
	return undefined;
}

export function useSaveAccountCertificates() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (body: SaveCertificatesRequest) =>
			saveAccountCertificates(body),
		onSuccess: async (data: SaveCertificatesResponse) => {
			const accountId = normalizeAccountCardId(data.account_id);
			await Promise.all([
				queryClient.invalidateQueries({ queryKey: ["certificates"] }),
				queryClient.invalidateQueries({
					queryKey: accountCardQueryKey(accountId),
				}),
			]);
		},
	});
}
